import { keepPreviousData, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useMemo } from 'react'

import { ApiPagedPayload } from '@/api/shared/types'
import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import { FilterFormData } from '@/components/pages/bill/ui/BillsFiltersPopover/BillsFiltersPopover.types'

import { FinanceService } from '../lib/finance.service'
import { InvoiceTypeAlias } from '../lib/finance.types'

export const FINANCE_INVOICE_LIST_QUERY_KEY = 'finance-invoice-list'

type Props = {
  isPaid?: boolean
  searchQuery?: string
  filters?: FilterFormData
  merchantId?: string
  enabled?: boolean
  invoiceTypeAlias?: InvoiceTypeAlias[]
  personalAccountId?: string
}

export const useFinanceInvoiceList = ({
  isPaid,
  searchQuery,
  filters,
  merchantId,
  pageSize,
  page,
  personalAccountId,
  invoiceTypeAlias,
  enabled
}: ApiPagedPayload<Props>) => {
  const { selectedProjectId: projectId } = useSelectedProjectStore()

  const { status, data, error, isLoading, isFetching, refetch, isError } =
    useQuery({
      queryKey: [
        FINANCE_INVOICE_LIST_QUERY_KEY,
        projectId,
        page,
        isPaid,
        searchQuery,
        filters?.invoicePeriod?.from,
        filters?.invoicePeriod?.to,
        filters?.invoicesTypes,
        filters?.invoicesCustomers,
        filters?.invoicesPremises,
        personalAccountId,
        merchantId,
        enabled
      ],
      queryFn: () =>
        FinanceService.getInvoices({
          projectId,
          page,
          pageSize,
          isPaid,
          query: searchQuery,
          periodFrom: filters?.invoicePeriod?.from
            ? dayjs(filters?.invoicePeriod?.from).format('YYYY-MM')
            : undefined,
          periodTo: filters?.invoicePeriod?.to
            ? dayjs(filters?.invoicePeriod?.to).format('YYYY-MM')
            : undefined,
          invoiceTypeAlias: filters?.invoicesTypes || invoiceTypeAlias,
          customerId: filters?.invoicesCustomers,
          premiseId: filters?.invoicesPremises,
          merchantId,
          personalAccountId
        }),
      placeholderData: keepPreviousData,
      staleTime: 0,
      enabled
    })

  const invoices = useMemo(() => data?.data?.data ?? [], [data?.data?.data])

  return {
    invoices,
    totalPages: data?.data?.totalPages,
    currentPage: data?.data?.page,
    textMessage: data?.statusText,
    data,
    error,
    isLoading,
    isFetching,
    status,
    isError,
    refetch
  }
}
